import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';

import { useSession } from '../../../app/useSession';
import { useTeamMember } from '../../../app/useTeamMember';
import { useToast } from '../../../components/Toast';
import { dateStamp, downloadBlob, toCsv } from '../../../lib/csv';
import { ConvertLeadModal } from './ConvertLeadModal';
import { LeadBulkBar } from './LeadBulkBar';
import { LeadDrawer } from './LeadDrawer';
import { LeadFilters } from './LeadFilters';
import { LeadList } from './LeadList';
import type { LeadDisplayStatus, LeadFilters as LeadFiltersValue, LeadRow } from './leads.schema';
import { useBulkSoftDeleteLeads, useBulkUpdateLeadStatus, useLeads, useLeadSources } from './leadsQueries';
import styles from './leads.module.css';

const DEFAULT_FILTERS: LeadFiltersValue = {
  search: '',
  statuses: [],
  sourceIds: [],
  scoreClasses: [],
};

export function LeadsPage() {
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const userId = sessionObj?.user.id ?? null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;
  const toast = useToast();
  const [filters, setFilters] = useState<LeadFiltersValue>(DEFAULT_FILTERS);
  const { data: leads = [], isLoading, error } = useLeads(orgId, filters);
  const { data: sources = [] } = useLeadSources(orgId);
  const bulkUpdateStatus = useBulkUpdateLeadStatus(orgId);
  const bulkSoftDelete = useBulkSoftDeleteLeads(orgId);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<LeadRow | null>(null);
  const [convertingLead, setConvertingLead] = useState<LeadRow | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const visibleIds = useMemo(() => leads.map((lead) => lead.id), [leads]);
  const selectedVisibleIds = useMemo(() => visibleIds.filter((id) => selectedIds.has(id)), [selectedIds, visibleIds]);
  const bulkBusy = bulkUpdateStatus.isPending || bulkSoftDelete.isPending;

  function openCreate() {
    setSelectedLead(null);
    setDrawerOpen(true);
  }

  function openEdit(lead: LeadRow) {
    setSelectedLead(lead);
    setDrawerOpen(true);
  }

  function closeDrawer() {
    setDrawerOpen(false);
    setSelectedLead(null);
  }

  function toggleLead(id: string) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllVisible() {
    setSelectedIds((current) => {
      const next = new Set(current);
      const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => next.has(id));
      if (allVisibleSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }

  async function updateSelectedStatus(status: LeadDisplayStatus, label: string) {
    try {
      await bulkUpdateStatus.mutateAsync({ ids: selectedVisibleIds, status });
      setSelectedIds(new Set());
      toast.success(`הסטטוס עודכן ל-${label}`);
    } catch {
      toast.error('לא הצלחנו לעדכן · נסה שוב');
    }
  }

  function exportSelectedToCsv() {
    const sourceById = sources.reduce<Record<string, string>>((acc, source) => {
      acc[source.id] = source.name;
      return acc;
    }, {});
    const selected = leads.filter((lead) => selectedIds.has(lead.id));
    const rows = selected.map((lead) => ({
      full_name: lead.full_name,
      company_name: lead.company_name ?? '',
      email: lead.email ?? '',
      phone: lead.phone ?? '',
      source: lead.source_id ? (sourceById[lead.source_id] ?? '') : '',
      score: lead.score ?? '',
      status: lead.status,
      received_at: lead.received_at,
    }));
    const blob = toCsv(rows, [
      { key: 'full_name', header: 'שם' },
      { key: 'company_name', header: 'חברה' },
      { key: 'email', header: 'אימייל' },
      { key: 'phone', header: 'טלפון' },
      { key: 'source', header: 'מקור' },
      { key: 'score', header: 'ציון' },
      { key: 'status', header: 'סטטוס' },
      { key: 'received_at', header: 'נקלט' },
    ]);
    downloadBlob(blob, `leads_${dateStamp()}.csv`);
    toast.success(`יוצאו ${String(rows.length)} לידים`);
  }

  async function deleteSelected() {
    if (!window.confirm(`למחוק ${String(selectedVisibleIds.length)} לידים?`)) return;

    try {
      await bulkSoftDelete.mutateAsync(selectedVisibleIds);
      const count = selectedVisibleIds.length;
      setSelectedIds(new Set());
      toast.success(`${String(count)} לידים נמחקו`);
    } catch {
      toast.error('לא הצלחנו לעדכן · נסה שוב');
    }
  }

  return (
    <>
      <div className={styles.pageHead}>
        <div>
          <h1>לידים</h1>
          <p className={styles.pageSub}>פניות חדשות לפני שהן הופכות לדילים.</p>
        </div>
        <button type="button" className={styles.btnPrimary} onClick={openCreate}>
          <Plus size={16} aria-hidden="true" />
          ליד
        </button>
      </div>

      <LeadFilters filters={filters} sources={sources} onChange={setFilters} />

      {isLoading ? <p className={styles.notice}>טוען לידים...</p> : null}
      {error ? <p className={styles.notice}>לא הצלחנו לטעון לידים · נסה שוב</p> : null}

      {selectedVisibleIds.length > 0 ? (
        <LeadBulkBar
          selectedCount={selectedVisibleIds.length}
          disabled={bulkBusy}
          onStatusChange={(status, label) => {
            void updateSelectedStatus(status, label);
          }}
          onDelete={() => {
            void deleteSelected();
          }}
          onExportCsv={exportSelectedToCsv}
          onClear={() => {
            setSelectedIds(new Set());
          }}
        />
      ) : null}

      {!isLoading && !error && leads.length === 0 ? (
        <div className={styles.emptyCard}>
          <div className={styles.emptySmiley} aria-hidden="true">
            <svg viewBox="0 0 64 64" width="44" height="44">
              <circle cx="32" cy="32" r="28" fill="none" />
              <path
                d="M20 25c2-2 6-2 8 0M36 25c2-2 6-2 8 0M22 39c4 5 16 5 20 0"
                fill="none"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <p>אין לידים עדיין · קלוט את הראשון בלחיצה על «+ ליד»</p>
          <button type="button" className={styles.btnPrimary} onClick={openCreate}>
            <Plus size={16} aria-hidden="true" />
            ליד
          </button>
        </div>
      ) : null}

      {!isLoading && !error && leads.length > 0 ? (
        <LeadList
          leads={leads}
          sources={sources}
          selectedIds={selectedIds}
          onToggleLead={toggleLead}
          onToggleAllVisible={toggleAllVisible}
          onSelect={openEdit}
        />
      ) : null}

      <LeadDrawer
        open={drawerOpen}
        lead={selectedLead}
        sources={sources}
        orgId={orgId}
        userId={userId}
        onClose={closeDrawer}
        onRequestConvert={(lead) => {
          setConvertingLead(lead);
          setDrawerOpen(false);
        }}
      />

      <ConvertLeadModal
        open={convertingLead !== null}
        lead={convertingLead}
        orgId={orgId}
        userId={userId}
        onClose={() => {
          setConvertingLead(null);
        }}
      />
    </>
  );
}
